import { VALTYPES } from '../../../../data/interfaces';
import { numericalAggregatorList } from '../../helpers/aggregators';
import { categoricalOrderOptions } from '../../helpers/categoricalSorters';
import {
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeNumericalColorScheme,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { MultiNumericalPunchCard } from './MultiNumericalPunchCard';

export const manifestMultiNumericalPunchCard: TpChartManifest = {
  id: 'MultiNumericalPunchCard',
  name: 'Heat map dot plot',
  description: `Shows the **relationship** between a **categorical property** and a **number of numerical properties**.
  - Each state of the categorical property defines a row in the dot plot.
  - Each numerical property defines a column in the dot plot.
  - For each cell, a disc is shown with the size proportional to a chosen aggregation over all values of that property for that categorical state.
  - The color of the disc can be made to reflect another aggregation.

Example usage:
- For single cell data, this can be used to visualize expression values for a number of cell types (categorical property, rows) over a number of genes (numerical properties, columns).
`,
  component: MultiNumericalPunchCard,
  canvasBased: true,
  groups: [
    { groupId: 'cats', groupName: 'Categories' },
    { groupId: 'props', groupName: 'Properties' },
    { groupId: 'disc', groupName: 'Disc size' },
    { groupId: 'color', groupName: 'Disc color' },
  ],
  aspects: [
    {
      id: 'categories',
      name: 'Categories data',
      description: 'The states of this property define the rows of the dot plot.',
      groupId: 'cats',
      required: true,
      quality: 1,
      dataType: VALTYPES.CATEGORICAL,
    },
  ],
  compoundAspects: [
    {
      id: 'properties',
      name: 'Properties',
      description: 'This set of numerical properties defines the columns of the dot plot.',
      groupId: 'props',
      required: true,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
      minCount: 1,
    },
  ],
  configParams: [
    {
      id: 'discSizeAggregator',
      name: 'Disc size aggregation type',
      description: `Defines the type of aggregation used to display the disc size in the dot plot.
This aggregation is applied to all numerical property values for a specific categorical state.`,
      groupId: 'disc',
      paramType: createConfigParamMultipleChoice(
        numericalAggregatorList.map((agg) => ({
          id: agg.id,
          name: agg.displayName,
        })),
      ),
    },
    {
      id: 'discColorAggregator',
      name: 'Disc color aggregation type',
      description: `Defines the type of aggregation used to display the disc color in the dot plot.
This aggregation is applied to all numerical property values for a specific categorical state.`,
      groupId: 'color',
      paramType: createConfigParamMultipleChoice(
        numericalAggregatorList.map((agg) => ({
          id: agg.id,
          name: agg.displayName,
        })),
      ),
    },
    {
      id: 'discColorTransformation',
      name: 'Disc color transformation',
      description: `Specifies an optional normalizing transformation applied to the aggregation value used to define the colors of the discs.`,
      groupId: 'color',
      paramType: createConfigParamMultipleChoice([
        {
          id: 'none',
          name: 'None',
        },
        {
          id: 'columnNormalized',
          name: 'Column normalized',
        },
      ]),
    },
    {
      id: 'gammaRadius',
      name: 'Size gamma factor',
      description: `Scaling factor that defines a nonlinear relation between the aggregation value of a cell and the displayed disc size. 
This can be used to emphasise cells with small values.`,
      groupId: 'disc',
      paramType: createConfigParamTypeRange(0, 10, 0.1, 5),
    },

    {
      id: 'sortCatsBy',
      name: 'Sort by',
      description: `Specifies how the categorical states, defining the rows, should be sorted.`,
      groupId: 'cats',
      paramType: createConfigParamMultipleChoice(categoricalOrderOptions),
    },
    {
      id: 'labelSpaceCats',
      name: 'Label space (categories)',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the rows (categorical states).',
      groupId: 'cats',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'labelSpaceProps',
      name: 'Label space (properties)',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the columns (numerical properties).',
      groupId: 'props',
      paramType: createConfigParamLabelSpace(),
    },

    {
      id: `colorSchemeNumerical`,
      name: `Color scheme`,
      description: `Continuous color palette to be used to map the aggregated values to colors.`,
      groupId: 'color',
      paramType: createConfigParamTypeNumericalColorScheme(),
    },
    {
      id: `colorGammaNumerical`,
      name: `Color gamma factor`,
      description: `Scaling factor that defines a nonlinear relation between the aggregation value of a cell and the displayed color. 
This can be used to emphasise cells with small values.`,
      groupId: 'color',
      paramType: createConfigParamTypeRange(1, 10, 0.25, 5),
    },
  ],
  examples: [
    {
      id: 'multinumerical_punchcard_1',
    },
  ],
};
